export { SharePointList, BasePermissionEnum };

const BasePermissionEnum = {
	EmptyMask: 0,
	ViewListItems: 1,
	AddListItems: 2,
	EditListItems: 3,
	DeleteListItems: 4,
	ApproveItems: 5,
	OpenItems: 6,
	ViewVersions: 7,
	DeleteVersions: 8,
	CancelCheckout: 9,
	ManagePersonalViews: 10,
	ManageLists: 12,
	ViewFormPages: 13,
	AnonymousSearchAccessList: 14,
	Open: 17,
	ViewPages: 18,
	AddAndCustomizePages: 19,
	ApplyThemeAndBorder: 20,
	ApplyStyleSheets: 21,
	ViewUsageData: 22,
	CreateSSCSite: 23,
	ManageSubwebs: 24,
	CreateGroups: 25,
	ManagePermissions: 26,
	BrowseDirectories: 27,
	BrowseUserInfo: 28,
	AddDelPrivateWebParts: 29,
	UpdatePersonalWebParts: 30,
	ManageWeb: 31,
	AnonymousSearchAccessWebLists: 32,
	UseClientIntegration: 37,
	UseRemoteAPIs: 38,
	ManageAlerts: 39,
	CreateAlerts: 40,
	EditMyUserInfo: 41,
	EnumeratePermissions: 63,
	FullMask: 65,
};

// eslint-disable-next-line one-var
const isUniqueId = (id) => Boolean(/^[A-Za-z0-9]{8}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{12}$/.test(id));



class SharePointList {
	constructor(isListNotLibrary, siteRelativePaths, internalNames, titles, listItemEntityTypeFullName, effectivePermissions) {
		this.isListNotLibrary = isListNotLibrary;
		this._siteRelativePaths = siteRelativePaths;
		this._internalNames = internalNames;
		this._titles = titles;
		this._listItemEntityTypeFullName = listItemEntityTypeFullName;
		this.content = null; // (expected [])
		this.contentLoading = false;
		this.contentLoaded = false;
		this._effectivePermissions = effectivePermissions;
	}

	get effectivePermissions() {
		return this._effectivePermissions;
	}

	get internalNames() {
		return this._internalNames;
	}

	get userCanAddItems() {
		return this.hasPermission(BasePermissionEnum.AddListItems);
	}

	get userCanEditItems() {
		return this.hasPermission(BasePermissionEnum.EditListItems);
	}

	get userPermissions() {
		return Object.entries(BasePermissionEnum).filter((bp) => this.hasPermission(bp[1]));
	}

	_escapedTitle(environment) {
		return this._title(environment).replace(/'/g, "''");
	}

	_fieldsEndpoint(environment, fieldTitle) {
		return `${this._siteRelativePath(environment)}_api/web/lists/GetByTitle('${this._escapedTitle(environment)}')/Fields/GetByTitle('${fieldTitle}')`;
	}

	_fileEndpoint(environment, libraryRootRelativeFolder, fileName) {
		return `${this._siteRelativePath(environment)}_api/web/GetFileByServerRelativeUrl('/sites/intranet/${this._siteRelativePath(environment)}${this._internalName(environment)}/${libraryRootRelativeFolder}/${fileName}')`;
	}

	_folderEndpoint(environment, libraryRootRelativeFolder) {
		return `${this._siteRelativePath(environment)}_api/web/GetFolderByServerRelativeUrl('/sites/intranet/${this._siteRelativePath(environment)}${this._internalName(environment)}/${libraryRootRelativeFolder}')`;
	}

	_foldersEndpoint(environment) {
		return `${this._siteRelativePath(environment)}_api/web/folders`;
	}

	_internalName(testEnvironment) {
		return (Array.isArray(this._internalNames)) ? (testEnvironment ? this._internalNames[0] : this._internalNames[1]) : this._internalNames;
	}

	_itemFileEndpoint(environment, itemId) {
		return `${this._itemsEndpoint(environment, itemId)}/File`;
	}

	_itemsEndpoint(environment, itemId) {
		if (itemId && isUniqueId(itemId)) {
			return `${this._siteRelativePath(environment)}_api/web/lists/GetByTitle('${this._escapedTitle(environment)}')/GetItemByUniqueId('${itemId}')`;
		}
		return `${this._siteRelativePath(environment)}_api/web/lists/GetByTitle('${this._escapedTitle(environment)}')/Items` + (itemId ? `(${itemId})` : '');
	}

	_queryString(itemId, fields, parameters) {
		let _parameters = {
				...parameters,
			}, _itemFields, _expand = [], fieldsToSplitAndExpand;

		if (fields) {
			if (typeof (fields) === 'string') {
				_itemFields = [fields];
			} else if (Array.isArray(fields)) {
				_itemFields = fields;
			} else {
				_itemFields = fields.itemFields;
			}
			_parameters.select = [(_itemFields || ['*']).map((f) => `${f}`).join(','), (fields.versionsFields || []).map((f) => `Versions/${f}`).join(','), (fields.attachmentFilesFields || []).map((f) => `AttachmentFiles/${f}`).join(',')].filter((el) => el).join(',');

			if (fields.versionsFields) {
				_expand.push('Versions');
			}
			if (fields.attachmentFilesFields) {
				_expand.push('AttachmentFiles');
			}
			if (_itemFields && (fieldsToSplitAndExpand = _itemFields.filter((f) => f.indexOf('/') > 0)).length) {
				_expand.push(...new Set(fieldsToSplitAndExpand.map((f) => f.split('/')[0])));
			}
			if (_expand.length) {
				_parameters.expand = _expand;
			}
		}

		if (!itemId && (itemId !== 0) && !_parameters.top) {
			_parameters.top = 5000;
		}

		return Object.keys(_parameters).filter((p) => _parameters[p] !== null).map((p, pIdx) => `${(pIdx ? '' : '?')}$${p}=${_parameters[p]}`).join('&');
	}

	_siteRelativePath(testEnvironment) {
		return (Array.isArray(this._siteRelativePaths)) ? (testEnvironment ? this._siteRelativePaths[0] : this._siteRelativePaths[1]) : this._siteRelativePaths;
	}

	_title(testEnvironment) {
		return this._titles ? ((Array.isArray(this._titles)) ? (testEnvironment ? this._titles[0] : this._titles[1]) : this._titles) : this._internalName(testEnvironment);
	}

	field(environment, fieldTitle) {
		return this._fieldsEndpoint(environment, fieldTitle);
	}

	fileAbsolutePath(environment, libraryRootRelativeFolder, fileName) {
		return `${this.siteAbsolutePath(environment)}/${libraryRootRelativeFolder}/${fileName}`;
	}

	fileContent(environment, folderName, fileName) {
		return `${this._fileEndpoint(environment, folderName, fileName)}/$value`;
	}

	filesAdd(environment, folderName, fileName) {
		return `${this._siteRelativePath(environment)}_api/web/lists/GetByTitle('${this._escapedTitle(environment)}')/RootFolder/Folders('${folderName}')/Files/Add(url='${fileName}',overwrite=true)`;
	}

	folder(environment, libraryRootRelativeFolder) {
		return this._folderEndpoint(environment, libraryRootRelativeFolder);
	}

	folderFiles(environment, libraryRootRelativeFolder, fields, parameters) {
		return `${this._folderEndpoint(environment, libraryRootRelativeFolder)}/Files${this._queryString(true, fields, parameters)}`;
	}

	folders(environment) {
		return this._foldersEndpoint(environment);
	}

	hasPermission(perm) {
		switch (perm) {
			case BasePermissionEnum.emptyMask:
				return true;
			case BasePermissionEnum.fullMask:
				if ((this.effectivePermissions.high & 32767) === 32767) return this.effectivePermissions.low === 65535;
				return false;
			default: {
				let n = perm - 1,
					val = 1;
				if (n >= 0 && n < 32) return 0 !== (this.effectivePermissions.low & (val << n));
				if (n >= 32 && n < 64) return 0 !== (this.effectivePermissions.high & (val << n - 32));
				return false;
			}
		}
	}

	internalName(environment) {
		return this._internalName(environment);
	}

	itemAttachFile(environment, itemId, fileName) {
		return `${this._itemsEndpoint(environment, itemId)}/AttachmentFiles/add(FileName='${fileName}')`;
	}

	itemFile(environment, itemId, fields, parameters) {
		return this._itemFileEndpoint(environment, itemId) + this._queryString(itemId, fields, parameters);
	}

	items(environment, itemId, fields, parameters) {
		return this._itemsEndpoint(environment, itemId) + this._queryString(itemId, fields, parameters);
	}

	metadata(environment) {
		return ({
			__metadata: {
				type: this._listItemEntityTypeFullName || `SP.Data.${this._internalName(environment)}${this.isListNotLibrary ? 'ListItem' : 'Item'}`,
			},
		});
	}

	parsePermissions() {
		return Object.keys(BasePermissionEnum).reduce((array, key) => (this.hasPermission(BasePermissionEnum[key]) ? array.concat(key.toString()) : array), []);
	}

	siteAbsolutePath(environment) {
		return `${SharePointList.siteCollectionPath}/${this._siteRelativePath(environment)}${this._internalName(environment)}`;
	}

	title(environment) {
		return this._title(environment);
	}

	static initialize({ siteCollectionPath }) {
		SharePointList.siteCollectionPath = siteCollectionPath;
	}
}
